'use client';
import type { ReactNode } from 'react';
import { SectLabel, SectH2, SectLede } from '@/components/landing/v4-shared';
import { useV4Enter } from '@/hooks/useV4Enter';

/**
 * HowItWorksPillar — canonical 3-step grid for the two pillar pages
 * (/simple-setup and /ai-business-page). Mirrors the homepage's
 * `HowItWorksSection` exactly:
 *   - 3 columns @ ≥1100px, single-column ≤1100px
 *   - 72px circle per step with per-step accent (cyan / amber / jade)
 *   - tri-pillar gradient connector (cyan → amber → jade) behind the circles
 *   - per-step deep-link to `#${anchor}` so the rich step components below
 *     act as the "expanded view" target
 *
 * The connector colour stays tri-pillar regardless of which pillar page
 * hosts it — the homepage does the same and the visual canon is shared.
 *
 * Props are deliberately minimal so each pillar page can wire its own
 * `*_COPY.sections` slugs without leaking pillar-specific tokens here.
 */

const PILLAR_TITLE_COLOR: Record<'cyan' | 'amber' | 'jade', string> = {
  cyan: 'var(--cyan-glow)',
  amber: 'var(--amber-glow)',
  jade: 'var(--jade-glow)',
};

// Per-step accent — mirrors the homepage `STEP_COLORS` so the visual rhythm
// is identical between homepage / pillar pages. Step 1 cyan, step 2 amber,
// step 3 jade.
const STEP_COLORS: ReadonlyArray<string> = [
  'var(--cyan-glow)',
  'var(--amber-glow)',
  'var(--jade-glow)',
];

export interface HowItWorksPillarStep {
  readonly num: string;
  readonly title: string;
  readonly body: string;
  readonly anchor: string;
}

interface Props {
  readonly pillar: 'cyan' | 'amber' | 'jade';
  readonly eyebrow: string;
  readonly heading: ReactNode;
  readonly lede?: string;
  readonly steps: ReadonlyArray<HowItWorksPillarStep>;
}

export function HowItWorksPillar({ pillar, eyebrow, heading, lede, steps }: Props) {
  const wrapperRef = useV4Enter();

  return (
    <section
      data-v4-section="how-it-works-pillar"
      aria-labelledby="how-it-works-pillar-heading"
      style={{ paddingTop: 96, paddingBottom: 96 }}
    >
      <div
        style={{
          maxWidth: 1100,
          marginInline: 'auto',
          paddingInline: 24,
        }}
      >
        {/* Centered header */}
        <div style={{ maxWidth: 720, margin: '0 auto 64px', textAlign: 'center' }}>
          <SectLabel color={PILLAR_TITLE_COLOR[pillar]}>{eyebrow}</SectLabel>
          <SectH2
            id="how-it-works-pillar-heading"
            style={{ margin: '0 auto 24px' }}
          >
            {heading}
          </SectH2>
          {lede ? <SectLede style={{ margin: '0 auto 0' }}>{lede}</SectLede> : null}
        </div>

        {/* Steps row */}
        <div ref={wrapperRef} style={{ position: 'relative', margin: '0 auto' }}>
          {/* Tri-pillar gradient connector behind the circles */}
          <div
            className="v4-step-connector"
            style={{
              position: 'absolute',
              top: 36,
              left: '18%',
              right: '18%',
              zIndex: 0,
            }}
          />

          <ol
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 48,
              listStyle: 'none',
              padding: 0,
              margin: 0,
            }}
            className="v4-hiw-steps v4-stagger"
          >
            {steps.map((step, i) => {
              const accent = STEP_COLORS[i] ?? STEP_COLORS[STEP_COLORS.length - 1];
              return (
                <li
                  key={step.num}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    textAlign: 'center',
                    gap: 16,
                    position: 'relative',
                    zIndex: 1,
                  }}
                >
                  <a
                    href={`#${step.anchor}`}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      textAlign: 'center',
                      gap: 16,
                      textDecoration: 'none',
                      color: 'inherit',
                    }}
                  >
                    {/* Circle */}
                    <div
                      aria-hidden="true"
                      style={{
                        width: 72,
                        height: 72,
                        borderRadius: '50%',
                        border: `2px solid ${accent}`,
                        background: 'var(--ink-2)',
                        boxShadow: `0 0 30px -10px ${accent}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontFamily: 'var(--font-geist-mono)',
                        fontWeight: 500,
                        fontSize: 18,
                        color: accent,
                      }}
                    >
                      {step.num}
                    </div>

                    <h3
                      style={{
                        fontFamily: 'var(--font-geist-sans)',
                        fontSize: 24,
                        fontWeight: 500,
                        color: 'var(--paper)',
                        margin: 0,
                      }}
                    >
                      {step.title}
                    </h3>
                    <p
                      style={{
                        fontFamily: 'var(--font-geist-sans)',
                        fontSize: 15,
                        color: 'var(--dim)',
                        lineHeight: 1.55,
                        maxWidth: '30ch',
                        margin: 0,
                      }}
                    >
                      {step.body}
                    </p>
                  </a>
                </li>
              );
            })}
          </ol>
        </div>
      </div>
      <style>{`
        @media (max-width: 1100px) {
          [data-v4-section="how-it-works-pillar"] .v4-hiw-steps {
            grid-template-columns: 1fr !important;
            gap: 40px !important;
          }
          [data-v4-section="how-it-works-pillar"] .v4-step-connector {
            display: none;
          }
        }
      `}</style>
    </section>
  );
}

export default HowItWorksPillar;
